/**
 * Page publique de partage de trajet — F-CNF-07, F-CNF-08 (SE-06, SE-07, SA-16).
 *
 * Aucun compte, aucun jeton : la seule preuve de droit est la possession du
 * lien. `SA-16` exige que l'expiration soit revérifiée à chaque requête —
 * c'est pourquoi cette page ne fait pas de rendu figé une fois pour toutes,
 * elle sonde `GET /partage/:pays/:jeton` à la cadence que le serveur
 * lui-même annonce (`sondageRepliSecondes`), qui vient du référentiel pays.
 *
 * Elle ne reçoit — donc ne dit — que la position du véhicule et l'heure
 * d'arrivée estimée : ni destination, ni chauffeur, ni véhicule, ni état de la
 * course (décision du porteur du 22 octobre 2026, SA-06). « Position connue »
 * suffit à choisir l'écran ; l'état n'a pas à voyager pour cela.
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import { creerTraducteur, type Langue, type Traducteur } from '@qanbio/i18n/partage';
import { habillage } from '@qanbio/design';

import { lirePartage, SECONDES_SONDAGE_PAR_DEFAUT, type ResultatPartage } from './api';
import { analyserChemin } from './chemin';
import { Carte } from './carte';

const ORIGINE_API = import.meta.env.VITE_API_ORIGIN;
const CLE_CARTE = import.meta.env.VITE_GOOGLE_MAPS_KEY;

function formatHeure(langue: Langue, date: Date): string {
  return new Intl.DateTimeFormat(langue, { timeStyle: 'short' }).format(date);
}

interface EcranLienFermeProps {
  readonly dejaVu: boolean;
  readonly traducteur: Traducteur;
}

/**
 * Un lien jamais existé et un lien fermé après une course terminée reçoivent
 * la même réponse 404 de l'API (SA-16) — c'est ici, avec la mémoire de la
 * session, qu'ils se distinguent. Deux appels littéraux, jamais une clé
 * choisie dynamiquement — F-LNG-02.
 */
function EcranLienFerme({ dejaVu, traducteur }: EcranLienFermeProps) {
  if (dejaVu) {
    return (
      <>
        <h1>{traducteur.traduire('partage.expire.titre')}</h1>
        <p className="sous">{traducteur.traduire('partage.expire.description')}</p>
      </>
    );
  }
  return (
    <>
      <h1>{traducteur.traduire('partage.introuvable.titre')}</h1>
      <p className="sous">{traducteur.traduire('partage.introuvable.description')}</p>
    </>
  );
}

interface EcranEnRouteProps {
  readonly langue: Langue;
  readonly traducteur: Traducteur;
  readonly arriveeEstimeeLe: Date | null;
}

/**
 * Tout ce que la page sait dire : le véhicule est en route, et à quelle heure il
 * devrait arriver. Ni destination, ni chauffeur, ni véhicule — l'API ne les
 * rend plus (décision du porteur du 22 octobre 2026, SA-16). L'heure est dite
 * « à destination » pour qu'elle ne se lise pas comme l'arrivée chez celui qui
 * regarde la page.
 */
function EcranEnRoute({ langue, traducteur, arriveeEstimeeLe }: EcranEnRouteProps) {
  return (
    <>
      <span className="direct">
        <i /> {traducteur.traduire('partage.pied.suivi_par')}
      </span>
      <h1>{traducteur.traduire('partage.en_route.titre')}</h1>
      <p className={arriveeEstimeeLe !== null ? 'eta' : 'sous'}>
        {arriveeEstimeeLe !== null
          ? traducteur.traduire('partage.arrivee_estimee', {
              heure: formatHeure(langue, arriveeEstimeeLe),
            })
          : traducteur.traduire('partage.arrivee_estimee_indisponible')}
      </p>
      <p className="mention">{traducteur.traduire('partage.mention_pas_d_installation')}</p>
    </>
  );
}

/** Pas de position : course pas encore affectée, ou relevé du véhicule trop ancien. */
function EcranPositionAbsente({ traducteur }: { readonly traducteur: Traducteur }) {
  return (
    <>
      <h1>{traducteur.traduire('partage.position_absente.titre')}</h1>
      <p className="sous">{traducteur.traduire('partage.position_absente.sous_titre')}</p>
    </>
  );
}

interface EtatPartage {
  readonly resultat: ResultatPartage | null;
  /**
   * A-t-on déjà vu ce lien vivant ? Le 404 d'un jeton qui n'a jamais existé
   * et celui d'une course terminée sont volontairement rendus identiques par
   * l'API (SA-16) — c'est à la page, qui a mémoire de la session, de rendre
   * « lien inconnu » et « trajet terminé » lisiblement différents.
   */
  readonly dejaVu: boolean;
}

function usePartage(pays: string | undefined, jeton: string | undefined): EtatPartage {
  const [etat, setEtat] = useState<EtatPartage>({ resultat: null, dejaVu: false });
  const intervalleRef = useRef(SECONDES_SONDAGE_PAR_DEFAUT);

  useEffect(() => {
    if (pays === undefined || jeton === undefined) return;
    let arrete = false;
    let minuteur: ReturnType<typeof setTimeout> | null = null;
    let dejaVu = false;
    const controleur = new AbortController();

    async function sonder() {
      const prochain = await lirePartage(ORIGINE_API, pays!, jeton!, controleur.signal);
      if (arrete) return;
      if (prochain.type === 'trouve') {
        intervalleRef.current = prochain.partage.sondageRepliSecondes;
        dejaVu = true;
      }
      setEtat({ resultat: prochain, dejaVu });
      if (prochain.type === 'introuvable') return; // terminal — SA-16, SE-07.
      minuteur = setTimeout(() => void sonder(), intervalleRef.current * 1_000);
    }

    void sonder();
    return () => {
      arrete = true;
      controleur.abort();
      if (minuteur !== null) clearTimeout(minuteur);
    };
  }, [pays, jeton]);

  return etat;
}

export function AppPartage() {
  // Français par défaut, comme les trois autres surfaces — CLAUDE.md §3.
  // Aucune donnée de profil à consulter ici : pas de compte, pas de session.
  const [langue, setLangue] = useState<Langue>('fr');
  const traducteur = useMemo(() => creerTraducteur(langue), [langue]);
  const chemin = useMemo(() => analyserChemin(window.location.pathname), []);
  const { resultat, dejaVu } = usePartage(chemin?.pays, chemin?.jeton);
  const { couleurs } = habillage('passager');

  const bascule = (
    <button
      type="button"
      className="langue"
      onClick={() => setLangue(langue === 'fr' ? 'en' : 'fr')}
    >
      {langue === 'fr'
        ? traducteur.traduire('commun.langue.anglais')
        : traducteur.traduire('commun.langue.francais')}
    </button>
  );

  if (chemin === null || resultat?.type === 'introuvable') {
    return (
      <main className="ecran ecran-sans-carte">
        {bascule}
        <div className="feuille">
          <EcranLienFerme dejaVu={dejaVu} traducteur={traducteur} />
        </div>
      </main>
    );
  }

  if (resultat === null) {
    return (
      <main className="ecran ecran-sans-carte">
        {bascule}
        <div className="feuille">
          <p className="sous">{traducteur.traduire('partage.chargement')}</p>
        </div>
      </main>
    );
  }

  if (resultat.type === 'erreur') {
    return (
      <main className="ecran ecran-sans-carte">
        {bascule}
        <div className="feuille">
          <h1>{traducteur.traduire('partage.erreur.titre')}</h1>
          <p className="sous">{traducteur.traduire('partage.erreur.description')}</p>
          <button type="button" className="bouton fantome" onClick={() => window.location.reload()}>
            {traducteur.traduire('commun.action.reessayer')}
          </button>
        </div>
      </main>
    );
  }

  const { partage } = resultat;

  return (
    <main className="ecran">
      {bascule}
      <Carte
        cleApi={CLE_CARTE}
        position={partage.position}
        couleurAccent={couleurs.accent}
        couleurContour={couleurs.surAccent}
        traducteur={traducteur}
      />
      <div className="feuille">
        {partage.position !== null ? (
          <EcranEnRoute
            langue={langue}
            traducteur={traducteur}
            arriveeEstimeeLe={partage.arriveeEstimeeLe}
          />
        ) : (
          <EcranPositionAbsente traducteur={traducteur} />
        )}
      </div>
    </main>
  );
}
